import { type FormikHelpers, useFormikContext } from 'formik';
import { useDebouncedCallback } from 'use-debounce';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Skeleton } from '@hivebrite/design-system/Skeleton';

import FilterByType from 'hb-react/frontoffice/apps/Filters/components/Item';
import type { FiltersBlocks } from 'hb-react/frontoffice/apps/Filters/types/filter';

import type { TopFilter } from '../hooks/useGetUsersFilters';
import type { FormValues } from '../types';

interface FiltersProps {
  clearFilters: (
    setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
  ) => void;
  filtersConfig?: {
    top: TopFilter[];
    overlay: FiltersBlocks;
  };
  handleOnMoreFiltersClick: () => void;
  hasError: boolean;
  isLoading: boolean;
  refetchFilters: () => void;
  selectedFiltersCount: number;
}

const FiltersIcon = () => (
  <FontAwesomeIcon
    iconName="sliders"
    height="16px"
    iconType={CATEGORIES.REGULAR}
  />
);

const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

const getIcon = (filterName) => (filterName === 'who' ? SearchIcon : undefined);

const Filters = ({
  clearFilters,
  filtersConfig,
  handleOnMoreFiltersClick,
  hasError,
  isLoading,
  refetchFilters,
  selectedFiltersCount,
}: FiltersProps) => {
  const { submitForm, setFieldValue } = useFormikContext();

  const handleOnClearFilters = () => {
    clearFilters(setFieldValue);
  };

  const debouncedSubmit = useDebouncedCallback(() => submitForm(), 600);

  if (hasError) {
    return (
      <Box alignItems="center" mb={5} flexDirection="column">
        <Box mb={2}>
          {s__(
            'NotifyPeople|An error occurred while loading filters. Please try again.',
          )}
        </Box>
        <Button onClick={refetchFilters}>{__('Retry')}</Button>
      </Box>
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box flexDirection="column" mb={{ _: 0, m: 3 }}>
      <Box
        alignItems={{ _: 'flex-start', m: 'flex-end' }}
        flexDirection={{ _: 'column', m: 'row' }}
        mb={{ _: 0, m: 3 }}
      >
        <Box
          flexGrow={1}
          flexDirection={{ _: 'column', m: 'row' }}
          width={{ _: '100%', m: 'auto' }}
        >
          {filtersConfig?.top?.map((filterConfig, index) => (
            <Box
              display="block"
              key={filterConfig.name}
              ml={{ _: 0, m: index ? 2 : 0 }}
              flexGrow={1}
            >
              <FilterByType
                icon={getIcon(filterConfig.name)}
                filter={filterConfig}
                onChange={debouncedSubmit}
                wrapperProps={{ mb: { _: 4, m: 0 } }}
              />
            </Box>
          ))}
        </Box>
        <Box ml={{ _: 0, m: 2 }} height="fit-content">
          <Button
            icon={FiltersIcon}
            mb={{ _: 6, m: 0 }}
            onClick={handleOnMoreFiltersClick}
            variant="secondary"
            size="large"
          >
            {selectedFiltersCount
              ? __('More filters • %{filtersCount}').i__({
                  filtersCount: selectedFiltersCount.toString(),
                })
              : __('More filters')}
          </Button>
        </Box>
      </Box>
      {selectedFiltersCount ? (
        <Box justifyContent="flex-end" mb={3}>
          <FakeLink onClick={handleOnClearFilters}>
            {__('Clear all filters')}
          </FakeLink>
        </Box>
      ) : null}
    </Box>
  );
};

const LoadingState = () => (
  <Box alignItems="center" mb={5}>
    <Box flexGrow={1}>
      <Skeleton height="65px" width="100%" />
      <Skeleton ml={2} mr={2} height="65px" width="100%" />
    </Box>
    <Button icon={FiltersIcon} variant="secondary" size="large" isDisabled>
      {__('More filters')}
    </Button>
  </Box>
);

export default Filters;
